/*
 * Copyright 2024 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { foundryPlatformFetch as $foundryPlatformFetch } from "@osdk/shared.net.platformapi";
//
const _list = [0, "/v2/ontologies/{0}/actionTypes", 2];
/**
 * Lists the action types for the given Ontology.
 *
 * Each page may be smaller than the requested page size. However, it is guaranteed that if there are more
 * results available, at least one result will be present in the response.
 *
 * @public
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/actionTypes
 */
export function list($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _list, ...args);
}
const _get = [0, "/v2/ontologies/{0}/actionTypes/{1}", 2];
/**
 * Gets a specific action type with the given API name.
 *
 * @public
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/actionTypes/{actionType}
 */
export function get($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _get, ...args);
}
const _getByRid = [0, "/v2/ontologies/{0}/actionTypes/byRid/{1}", 2];
/**
 * Gets a specific action type with the given RID.
 *
 * @public
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/actionTypes/byRid/{actionTypeRid}
 */
export function getByRid($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _getByRid, ...args);
}
const _getByRidBatch = [1, "/v2/ontologies/{0}/actionTypes/getByRidBatch", 3];
/**
 * Gets a list of action types by RID in bulk.
 *
 * Action types are filtered from the response if they don't exist or the requesting token lacks the required
 * permissions.
 *
 * The maximum batch size for this endpoint is 100.
 *
 * @beta
 *
 * Required Scopes: [api:ontologies-read]
 * URL: /v2/ontologies/{ontology}/actionTypes/getByRidBatch
 */
export function getByRidBatch($ctx, ...args) {
  return $foundryPlatformFetch($ctx, _getByRidBatch, ...args);
}